import { createRouter, createWebHistory } from "vue-router";
import thestore from "@/applibs/thestore.js";
import idleSession from "@/applibs/idleSession.js";
import Home from "../pages/Home.vue";

let maintenanceStatusCache = {
  value: null,
  checkedAt: 0,
};
let maintenanceRequestPromise = null;
const MAINTENANCE_STATUS_TTL = 60 * 1000;

async function serverMaintenanceChecker() {
  try {
    const res = await thestore.getServerStatus();
    return res.msg === "Server maintenance";
  } catch (error) {
    return error.response?.status == 503;
  }
}

function isMaintenancePath(path) {
  return path === "/maintenance" || path === "/Maintenance";
}

function getCachedMaintenanceStatus() {
  const now = Date.now();
  if (now - maintenanceStatusCache.checkedAt <= MAINTENANCE_STATUS_TTL) {
    return maintenanceStatusCache.value;
  }
  return null;
}

function refreshMaintenanceStatus() {
  if (maintenanceRequestPromise) {
    return maintenanceRequestPromise;
  }

  maintenanceRequestPromise = serverMaintenanceChecker()
    .then((isMaintenance) => {
      maintenanceStatusCache = {
        value: isMaintenance,
        checkedAt: Date.now(),
      };
      return isMaintenance;
    })
    .catch(() => {
      maintenanceStatusCache = {
        value: false,
        checkedAt: Date.now(),
      };
      return false;
    })
    .finally(() => {
      maintenanceRequestPromise = null;
    });

  return maintenanceRequestPromise;
}

const router = createRouter({
  history: createWebHistory(),
  mode: "history",
  scrollBehavior(_, _2, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
  routes: [
    { path: "/", component: Home, name: "Home" },

    {
      path: "/pages/term-and-conditions",
      component: () => import("../pages/p/TermAndConditions.vue"),
    },
    {
      path: "/pages/customer-agreement",
      component: () => import("../pages/p/CustomerAgreement.vue"),
    },
    {
      path: "/pages/risk-disclosure",
      component: () => import("../pages/p/RiskDisclosure.vue"),
    },
    {
      path: "/pages/trading-rules",
      component: () => import("../pages/p/TradingRules.vue"),
    },
    {
      path: "/pages/contact-us",
      component: () => import("../pages/p/ContactUs.vue"),
      name: "Contact Us",
    },
    {
      path: "/pages/privacy-policy",
      component: () => import("../pages/p/PrivacyPolicy.vue"),
      name: "Privacy Policy",
    },
    {
      path: "/maintenance",
      component: () => import("../pages/p/Maintenance.vue"),
      name: "Maintenance",
    },

    {
      path: "/Maintenance",
      component: () => import("../pages/p/Maintenance.vue"),
      name: "Maintenance",
    },
    {
      path: "/health",
      component: () => import("../pages/Health.vue"),
      name: "Health",
    },

    {
      path: "/auth/login",
      component: () => import("../pages/auth/login.vue"),
      name: "Login",
    },
    {
      path: "/auth/register",
      component: () => import("../pages/auth/register.vue"),
      name: "Register",
    },
    {
      path: "/auth/email-verification",
      component: () => import("../pages/auth/email-verification.vue"),
      name: "Email Verification",
    },
    {
      path: "/auth/email-verification-token-request",
      component: () =>
        import("../pages/auth/email-verification-token-request.vue"),
      name: "Email Verification request token",
    },
    {
      path: "/auth/forgot-password",
      component: () => import("../pages/auth/forgot-password.vue"),
      name: "Lupa Password",
    },
    {
      path: "/auth/passchange-form",
      component: () => import("../pages/auth/passchange-form.vue"),
      name: "Rubah Password",
    },
    {
      path: "/auth/two-factor-auth",
      component: () => import("../pages/auth/two-factor-auth.vue"),
      name: "Two factor",
    },

    {
      path: "/markets",
      component: () => import("../pages/markets/Index.vue"),
      name: "Market",
    },
    {
      path: "/markets/:market_id/:market_pair",
      component: () => import("../pages/markets/Exchange.vue"),
      name: "exchange",
    },

    {
      path: "/settings/index",
      component: () => import("../pages/Settings/Index.vue"),
      name: "Pengaturan",
    },
    {
      path: "/settings/profile",
      component: () => import("../pages/Settings/Profile.vue"),
      name: "settings_profile",
    },

    {
      path: "/settings/wallets/btc_deposit",
      component: () => import("../pages/Settings/Wallets/btc_deposit.vue"),
      name: "settings_wallets_btc_deposit",
    },
    {
      path: "/settings/wallets/btc_withdraw",
      component: () => import("../pages/Settings/Wallets/btc_withdraw.vue"),
      name: "settings_wallets_btc_withdraw",
    },

    {
      path: "/settings/bank",
      component: () => import("../pages/Settings/Bank.vue"),
      name: "settings_bank",
    },
    {
      path: "/settings/2fa",
      component: () => import("../pages/Settings/two-fa.vue"),
      name: "settings_2fa",
    },

    // {
    //   path: "/settings/bonus",
    //   component: SettingsBonus,
    //   name: "settings_bonus",
    // },

    {
      path: "/settings/wallets",
      component: () => import("../pages/Settings/Wallets/index.vue"),
      name: "settings_wallets",
    },
    {
      path: "/settings/wallets/:currency/deposit",
      component: () => import("../pages/Settings/Wallets/deposit.vue"),
      name: "settings_wallets_deposit",
    },
    {
      path: "/settings/wallets/:currency/withdraw",
      component: () => import("../pages/Settings/Wallets/withdraw.vue"),
      name: "settings_wallets_withdraw",
    },

    {
      path: "/settings/wallets/idr_deposit",
      component: () => import("../pages/Settings/Wallets/idr_deposit.vue"),
      name: "settings_wallets_idr_deposit",
    },
    {
      path: "/settings/wallets/idr_deposit/upload-file",
      component: () =>
        import("../pages/Settings/Wallets/idr_deposit_upload_file.vue"),
      name: "settings_wallets_idr_deposit_upload_file",
    },
    {
      path: "/settings/wallets/idr_withdraw",
      component: () => import("../pages/Settings/Wallets/idr_withdraw.vue"),
      name: "settings_wallets_idr_withdraw",
    },
    {
      path: "/settings/agreement-sign",
      component: () => import("../pages/Settings/AgreementSign.vue"),
      name: "settings_agreement_sign",
    },
    {
      path: "/settings/agreement-sign-two",
      component: () => import("../pages/Settings/AgreementSignTwo.vue"),
      name: "settings_agreement_sign_two",
    },

    // { path: "/about", component: About },
    // { path: "/p/faq", component: PageFaq },

    {
      path: "/kyc/nationality",
      component: () => import("../pages/kyc/nationality.vue"),
    },

    {
      path: "/kyc/id/satu",
      component: () => import("../pages/kyc/id/satu.vue"),
    },
    { path: "/kyc/id/dua", component: () => import("../pages/kyc/id/dua.vue") },
    {
      path: "/kyc/id/tiga",
      component: () => import("../pages/kyc/id/tiga.vue"),
    },
    {
      path: "/kyc/id/empat",
      component: () => import("../pages/kyc/id/empat.vue"),
    },

    {
      path: "/kyc/global/satu",
      component: () => import("../pages/kyc/global/satu.vue"),
    },
    {
      path: "/kyc/global/dua",
      component: () => import("../pages/kyc/global/dua.vue"),
    },
    {
      path: "/kyc/global/tiga",
      component: () => import("../pages/kyc/global/tiga.vue"),
    },
    {
      path: "/kyc/global/empat",
      component: () => import("../pages/kyc/global/empat.vue"),
    },
  ],
});
router.beforeEach(async function (to, _2, next) {
  try {
    if (idleSession.shouldRedirectHomeOnBoot()) {
      idleSession.clearHomeRedirectFlag();
      if (to.path !== "/") {
        return next("/");
      }
    }

    const cachedMaintenanceStatus = getCachedMaintenanceStatus();
    if (cachedMaintenanceStatus === null) {
      refreshMaintenanceStatus().then((isMaintenance) => {
        const currentPath = router.currentRoute.value.path;
        if (isMaintenance && !isMaintenancePath(currentPath)) {
          router.replace("/maintenance");
        } else if (!isMaintenance && isMaintenancePath(currentPath)) {
          router.replace("/");
        }
      });
    }

    if (cachedMaintenanceStatus === false && isMaintenancePath(to.path)) {
      // jika server tidak sedang maintenance tetapi user menuju path maintenance, user akan dilempar ke home

      return next("/");
    } else if (
      cachedMaintenanceStatus === true &&
      !isMaintenancePath(to.path)
    ) {
      // jika server sedang maintenance tetapi user menuju semua path kecuali path maintenance, user akan dilempar ke maintenance

      return next("/maintenance");
    }

    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);

    const appName = "PGX";
    const metaName = to.name || "Home";
    document.title = `${appName} | ${metaName}`;

    next();
  } catch (err) {
    // catch ini berfungsi jika terjadi error pada function serverMaintenanceChecker() maka halaman yang dituju tetap ditampilkan, jika tidak maka halaman akan blank

    next();
  }
});
export default router;
